import React, { useRef, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { Dialog, DialogContent, DialogTitle, Tooltip } from "@material-ui/core";
import { FileCopyOutlined } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";

import JidUtils from "../../utils/classes/JidUtils";

const useStyles = makeStyles(theme => ({
	infoSection: {
		display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
		gap: "5px",
	},

	root: { userSelect: "none" },

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": { marginRight: theme.spacing(1), },
	},

	btnWrapper: { position: "relative", },

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

	floatingButton: {
		transition: "transform 0.30s",
		"&:hover": { transform: "translateY(-5px)", },
	},

  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    gap: "1em",
  },

  infoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    gap: "1em",
    width: "100%",
  },

  copyButtonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    justifyContent: "center",
    padding: "1em",
  },

  copyButton: {
    cursor: "pointer",
    transition: "all 0.30s ease",
    "&:hover": { transform: "translateY(-5px)", },
  },
}));

const WhatsAppInfoModal = ({ open, onClose, whatsAppId, whatsAppSource }) => {
  //  ***************
  //  ** Variables **
  //  ***************
	const classes = useStyles();
  const timeoutVideoVisibility = useRef(null);
  const [copyTooltip, setCopyTooltip] = useState(i18n.t("versionModal.tooltips.copy"));
  const [whatsAppInfo, setWhatsAppInfo] = useState(null);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (whatsAppId) {
      const delayDebounceFN = setTimeout(() => {
        const fetchWhatsAppInfo = async () => {
          const { data } = await api.get(`/whatsappInfo/${whatsAppId}`);
          setWhatsAppInfo(data);
        };

        const fetchWhatsAppInfoOfficialApi = async () => {
          const { data } = await api.get(`/officialApi/clientApplicationPhoneNumber/${whatsAppId}`);
          setWhatsAppInfo(data);
        };

        if (whatsAppSource === 'OFFICIAL_API') void fetchWhatsAppInfoOfficialApi();
        else void fetchWhatsAppInfo();
      });

      return () => clearTimeout(delayDebounceFN); 
    }
  }, [whatsAppId, whatsAppSource]);

	

  //  ***************
  //  ** Functions **
  //  ***************
	const handleClose = () => {
    onClose();
  };

  const handleCopyInfo = async () => {
    const clipboardInfo = whatsAppSource === 'OFFICIAL_API'
      ? `${i18n.t('whatsappInfoModal.datas.metaWabaId')} ${whatsAppInfo.metaWabaId}\n${i18n.t('whatsappInfoModal.datas.metaPhoneNumberId')} ${whatsAppInfo.metaPhoneNumberId}`
      : `${i18n.t("whatsappInfoModal.datas.whatsAppUserName")} ${capitalizeFirstLetter(whatsAppInfo.name)}\n${i18n.t("whatsappInfoModal.datas.phoneNumber")} ${JidUtils.unformatJid(whatsAppInfo.id)}\n${i18n.t("whatsappInfoModal.datas.lid")} ${JidUtils.unformatJid(whatsAppInfo.lid)}\n${i18n.t("whatsappInfoModal.datas.username")} ${whatsAppInfo.username}`;

    await navigator.clipboard.writeText(clipboardInfo);

    setCopyTooltip(i18n.t("versionModal.tooltips.copied"));

    if (timeoutVideoVisibility.current) clearTimeout(timeoutVideoVisibility.current);
    timeoutVideoVisibility.current = setTimeout(() => setCopyTooltip(i18n.t("versionModal.tooltips.copy")), 700);
  };

  const renderInfo = () => {
    if (whatsAppSource === 'OFFICIAL_API') {
      return (
        <>
          <div className={classes.infoContainer}>
            <span>{i18n.t("whatsappInfoModal.datas.metaWabaId")} <b>{capitalizeFirstLetter(whatsAppInfo.metaWabaId)}</b></span>
            <span>{i18n.t("whatsappInfoModal.datas.metaPhoneNumberId")} <b>{JidUtils.unformatJid(whatsAppInfo.metaPhoneNumberId)}</b></span>
          </div>

          <div className={classes.copyButtonContainer}>
            <Tooltip title={copyTooltip} placement="top-start" arrow>
              <span className={classes.copyButton} onClick={async () => await handleCopyInfo()}>
                <FileCopyOutlined />
              </span>
            </Tooltip>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={classes.infoContainer}>
          <span>{i18n.t("whatsappInfoModal.datas.whatsAppUserName")} <b>{capitalizeFirstLetter(whatsAppInfo.name)}</b></span>
          <span>{i18n.t("whatsappInfoModal.datas.phoneNumber")} <b>{JidUtils.unformatJid(whatsAppInfo.id)}</b></span>
          <span>{i18n.t("whatsappInfoModal.datas.lid")} <b>{JidUtils.unformatJid(whatsAppInfo.lid)}</b></span>
          <span>{i18n.t("whatsappInfoModal.datas.username")} <b>{whatsAppInfo.username}</b></span>
        </div>

        <div className={classes.copyButtonContainer}>
          <Tooltip title={copyTooltip} placement="top-start" arrow>
            <span className={classes.copyButton} onClick={async () => await handleCopyInfo()}>
              <FileCopyOutlined />
            </span>
          </Tooltip>
        </div>
      </>
    );
  };



  //  ************
  //  ** Return **
  //  ************
	return (
		<div>
			<Dialog className={classes.root} open={open} onClose={handleClose} maxWidth="sm" scroll="paper">
				<DialogTitle>
          {i18n.t("whatsappInfoModal.title")}
				</DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
					{whatsAppInfo && renderInfo()}
        </DialogContent>
			</Dialog>
		</div>
	);
};

export default WhatsAppInfoModal;
